/**
 * Catalog lookup that degrades to English instead of to a raw key.
 *
 * next-intl's default for a missing key is to render the key itself, which on a
 * pricing page means a visitor sees `pricing.hero.heading` where the headline
 * should be. Every localised surface in this app therefore reads through an
 * accessor that falls back to the English source string it was generated from.
 *
 * That fallback is deliberately SILENT at render time and LOUD in CI: the
 * catalog sync gate (`npm run i18n:check`) is what fails when a key is missing,
 * so a translation gap is caught before deploy rather than shown to a user.
 *
 * Extracted because three modules had grown identical copies of it — server
 * marketing copy, client marketing copy, and now the app shell — and a fallback
 * that behaves differently in one of them is the kind of bug nobody looks for.
 */
export type TranslationValues = Record<string, string | number>;

export type Translator = (key: string, values?: TranslationValues) => string;

export function makeFallbackAccessor(t: Translator) {
  return (key: string, fallback: string, values?: TranslationValues): string => {
    try {
      const value = t(key, values);

      // next-intl echoes the key back when it cannot resolve it.
      return value === key ? formatFallback(fallback, values) : value;
    } catch {
      return formatFallback(fallback, values);
    }
  };
}

/**
 * Last-resort formatting for the English source when the catalog lookup failed.
 *
 * The gate (`npm run i18n:check`) is what keeps this path unreachable in
 * practice, but if it is ever reached the fallback must not leak ICU syntax at
 * a user — `{count, plural, one {Added # item} other {Added # items}}` is worse
 * than no message at all. This resolves simple `{name}` placeholders and picks
 * the `other` branch of a plural, which is the correct English form for every
 * count except one.
 *
 * It is NOT an ICU implementation and must never be used as one; next-intl owns
 * real formatting.
 */
function formatFallback(fallback: string, values?: TranslationValues): string {
  if (!values) return fallback;

  const plural = fallback.match(/^\{(\w+),\s*plural,.*\bother\s*\{([^{}]*)\}/);
  const text = plural ? plural[2].replace(/#/g, String(values[plural[1]] ?? '')) : fallback;

  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match
  );
}
